// Our goals for Arlo — well-behaved, no barking, comfortable, sociable, calm —
// and how his logs say each is going, with where every line comes from.

import { CheckCircle2, CircleDashed, CircleHelp, Flag, TriangleAlert } from 'lucide-react';
import { useMemo } from 'react';
import { GOALS } from '../../content/goals.ts';
import { SOCIAL_CATEGORIES } from '../../content/socialisation.ts';
import { useEvents, useLiveStatus } from '../../data/hooks.ts';
import { FROM_LABEL, checkGoals, type GoalCheck, type GoalStatus } from '../../domain/goals.ts';
import { SESSIONS_PER_DAY } from '../../domain/trainingplan.ts';
import { navigate } from '../../app/router.ts';
import { Card, CardHeader } from '../../ui/primitives.tsx';
import { FromTag, SourceList } from '../../ui/Sources.tsx';

const SOCIAL_TOTAL = SOCIAL_CATEGORIES.reduce((n, c) => n + c.items.length, 0);

export function useGoals(): GoalCheck[] {
  const events = useEvents();
  const s = useLiveStatus(10 * 60_000);
  return useMemo(() => checkGoals({ events, now: s.now, ageWeeks: s.weeks, socialTotal: SOCIAL_TOTAL, sessionsPerDay: SESSIONS_PER_DAY }), [events, s.now, s.weeks]);
}

const STATUS: Record<GoalStatus, { label: string; tone: string; icon: typeof Flag }> = {
  good: { label: 'On track', tone: 'good', icon: CheckCircle2 },
  building: { label: 'Building', tone: 'info', icon: CircleDashed },
  attention: { label: 'Needs a hand', tone: 'warn', icon: TriangleAlert },
  unknown: { label: 'Not enough logged', tone: 'neutral', icon: CircleHelp },
};

export function Goals() {
  const checks = useGoals();
  return (
    <div className="stack">
      <Card>
        <CardHeader title="Our goals for Arlo" icon={Flag} sub="What we’re working towards. Each one is checked against his last two weeks of logs; tags show where each figure comes from." />
        <div className="goal-strip goal-strip-wide">
          {checks.map((c) => (
            <GoalPill key={c.id} c={c} />
          ))}
        </div>
      </Card>
      {checks.map((c) => (
        <GoalCard key={c.id} c={c} />
      ))}
      <p className="muted small">The thresholds (for example “about three short sessions a day”) are the app’s rules of thumb, built on the guidance linked under each goal — not a test he passes or fails. Missing logs mean “not recorded”, not “didn’t happen”.</p>
    </div>
  );
}

function GoalCard({ c }: { c: GoalCheck }) {
  const goal = GOALS.find((g) => g.id === c.id)!;
  const st = STATUS[c.status];
  return (
    <Card className={`goal-card tone-${st.tone}`}>
      <header className="goal-head">
        <h3>{c.title}</h3>
        <span className={`goal-status tone-${st.tone}`}>
          <st.icon aria-hidden className="inline-icon" /> {st.label}
        </span>
      </header>
      <p className="muted small goal-aim">{goal.aim}</p>
      <p className="goal-headline">{c.headline}</p>
      {c.evidence.length > 0 && (
        <ul className="goal-evidence">
          {c.evidence.map((e) => (
            <li key={e.text}>
              <FromTag from={FROM_LABEL[e.from]} /> {e.text}
            </li>
          ))}
        </ul>
      )}
      <button type="button" className="goal-next" onClick={() => navigate(c.next.route)}>
        Next: {c.next.text} →
      </button>
      <details className="goal-how">
        <summary>How we get there</summary>
        <ul>
          {goal.how.map((h) => (
            <li key={h}>{h}</li>
          ))}
        </ul>
      </details>
      <SourceList refs={c.sources} label="Guidance" />
    </Card>
  );
}

function GoalPill({ c }: { c: GoalCheck }) {
  const st = STATUS[c.status];
  return (
    <button type="button" className={`goal-pill tone-${st.tone}`} onClick={() => navigate('/training/goals')} title={`${c.title}: ${c.headline}`}>
      <st.icon aria-hidden />
      <span>{c.title}</span>
    </button>
  );
}

/** Today's side column: the five goals at a glance. */
export function GoalsStrip() {
  const checks = useGoals();
  const help = checks.find((c) => c.status === 'attention');
  return (
    <Card className="goals-card" onClick={() => navigate('/training/goals')}>
      <span className="eyebrow">Our goals</span>
      <div className="goal-strip">
        {checks.map((c) => (
          <GoalPill key={c.id} c={c} />
        ))}
      </div>
      {help && (
        <p className="small goal-strip-note">
          <strong>{help.title}:</strong> {help.headline}
        </p>
      )}
    </Card>
  );
}
